<script>
    import CommunicationToggle from "../../../Components/Common/CommunicationToggle.svelte";
    import {
        CardLayout,
        CustomTooltip,
        DecimalInput,
        Loader,
        LoadingModal,
        NotificationModal,
        TableElementEditableStatus,
    } from "../../../Components/Common/index.svelte";
    import InfoIcon from "../../../Components/Common/InfoIcon.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { user, workersCompensation, isMobile } from "../../../stores";

    import { DirectInsuranceType } from "../../../Utils/utilEnums.svelte";

    import {
        getBoolean,
        getClaimStatus,
        getClaimERAStatus,
        getEligibilityStatusHealthFund,
        getEligibilityStatusMedicare,
        getText,
        getFormatDateAndTime,
        getBillingType,
        isObjectEmpty,
        showError,
        showException,
        goToDownloadPage,
    } from "../../../Utils/utilFunctions.svelte";
    import { isEnvNewOrApp } from "../../../Utils/utilRoute.svelte";

    import {
        getEncounterClaimStatusTooltip,
        showEncounterPendingClaimTooltip,
    } from "../helpers/encounterHelper.svelte";

    import { getUnderAndOverPaymentProperties } from "../helpers/encounterUtils.svelte";

    export let billingDetails = {};
    export let patientEncounterDetails = {};
    export let latestQuoteData = {},
        latestInvoiceData = {};
    export let reloadBillingDetails = () => {};
    export let isUpdatingBillingStatus = false;

    let isCheckingEligibility = false,
        isDownloadingEligibilityDoc = false,
        isDownloadingAssignmentDoc = false,
        isSendingClaimStatusNotification = false,
        isUpdatingStopDailyRetrieveReport = false,
        isClaimStatusNotificationModalOpen = false,
        isSentNotificationSuccessModalOpen = false,
        isStopDailyRetrieveReportModalOpen = false,
        isPartialPaymentCreated = false;

    let showClaimStatusErrorMsg = false;

    let showChangeDuplicateServiceOverrideModal = false,
        isUpdatingDuplicateServiceOverride = false,
        showDuplicateServiceOverrideTextInput = false;

    let isUpdatingSkipQuote = false;

    let showUnderPaymentText = false,
        underPaymentTexts = [],
        showOverPaymentText = false,
        overPaymentTexts = [];

    let underOverPaymentType = "",
        underOverPaymentAmount = 0,
        underOverPaymentFor = -1,
        showResetUnderOverPaymentModal = false,
        isResettingUnderOverPayment = false;

    $: billingData = { ...billingDetails.billingData };

    $: showDuplicateServiceOverrideText =
        _determineVisibilityOfDuplicateServiceOverrideText(billingData);

    $: hasDuplicateServiceOverrideInd =
        patientEncounterDetails.duplicateServiceOverrideInd;

    $: showDownloadEligibilityDisclaimerBtn =
        (billingDetails.eligibilityStatusMedicare == 1 ||
            billingDetails.eligibilityStatusHealthFund == 1) &&
        billingDetails.eligibilityStatusMedicare != 2 &&
        billingDetails.eligibilityStatusHealthFund != 2;

    $: showDownloadBulkBillAssignmentBtn =
        billingData.patientInsuranceType == 11 &&
        billingData.actualGap == 0 &&
        billingDetails.eligibilityStatusMedicare == 1;

    $: showCreatePartialPaymentBtn = _toggleShowCreatePartialPaymentBtn(
        latestQuoteData,
        latestInvoiceData,
    );

    $: showRequestInsuranceDeclineBtn = _toggleShowCreatePartialPaymentBtn(
        latestQuoteData,
        latestInvoiceData,
    );

    $: isEncounterClaimStatusFailed = billingDetails.claimStatus == 1;

    $: showCheckEligibilityBtn =
        billingData.id && billingData.mbsData?.length > 0;

    $: isUserDoctor = $user.role == 2;

    $: canUserEdit = $user.role >= 20;

    $: isUserAdmin = $user.role == 100;

    $: isUserEnableSeeClaimBtn = enableSeeClaimStatusBtnRoles.includes(
        $user.role,
    );

    $: isStopClaimERAStatusBtnVisible =
        isUserEnableSeeClaimBtn && billingDetails.claimERAStatus == 2;

    $: showNoGapProgram = isEnvNewOrApp();

    $: btnOptions = [
        {
            iconName: "notifyClaimStatusIcon",
            iFont: "fa-regular fa-bell",
            iconFunction: () => _openNotifyClaimStatusModal(),
            hasTooltip: true,
            tooltip: "Ask patient to pay total fee",
            btnText: "Notify patient",
            isHidden: !(
                enableSeeClaimStatusBtnRoles.includes($user.role) &&
                isEncounterClaimStatusFailed
            ),
        },
    ];

    $: initShowUnderAndOverPaymentText(patientEncounterDetails);

    const enableSeeClaimStatusBtnRoles = [20, 80, 100];

    const runEligibilityCheck = async () => {
        isCheckingEligibility = true;

        if (billingDetails.patientEncounterId)
            await apiMethods.postMethod
                .checkEligibility({
                    patientEncounterId: billingDetails.patientEncounterId,
                })
                .then(async (res) => {
                    if (res.error) showError(res.error);
                    else
                        await reloadBillingDetails(
                            billingDetails.patientEncounterId,
                        );
                });
        else
            showError(
                "Missing patient encounter id, please contact support...",
            );

        isCheckingEligibility = false;
    };

    const downloadEligibilityDoc = () => {
        goToDownloadPage(4, billingDetails.patientEncounterId);
    };

    const downloadAssignmentDoc = () => {
        goToDownloadPage(10, billingDetails.patientEncounterId);
    };

    const updateBillingSkipQuote = async (bool) => {
        isUpdatingSkipQuote = true;

        await apiMethods.putMethod
            .updatePatientEncounter({
                ...patientEncounterDetails,
                skipQuote: bool == 1,
                billingStatus: Math.max(
                    2,
                    patientEncounterDetails.billingStatus,
                ),
            }, false, "Set skip quote")
            .then((res) => {
                if (!res || !res.id || res.error)
                    showError(
                        "Failed to update billing details, please try again later or contact support",
                    );
                else reloadBillingDetails();
            })
            .catch((err) => showException(err));

        isUpdatingSkipQuote = false;
    };

    const sendClaimStatusNotification = async () => {
        isSendingClaimStatusNotification = true;

        await apiMethods.putMethod
            .sendPatientEncounterInsuranceDeclined(patientEncounterDetails.id)
            .then((res) => _handleSendClaimStatusNotification(res))
            .catch((err) => showException(err));

        _closeNotifyClaimStatusModal();

        isSendingClaimStatusNotification = false;
    };

    const stopDailyRetrieveReport = async () => {
        isUpdatingStopDailyRetrieveReport = true;

        patientEncounterDetails.claimERAStatus = 10;

        await apiMethods.putMethod
            .updateClaimEraStatus(patientEncounterDetails.id, 10)
            .then((res) => {
                if (!res || !res.id || res.error)
                    showError(
                        "Failed process, please refresh the page and try again and contact support if the issue persists.",
                    );
                else reloadBillingDetails();
            })
            .catch((err) => showException(err));

        _closeStopDailyRetrieveReportModal();

        isUpdatingStopDailyRetrieveReport = false;
    };

    const updateDuplicateServiceOverride = async () => {
        isUpdatingDuplicateServiceOverride = true;

        await apiMethods.putMethod
            .updateEncounterDuplicateServiceOverride({
                id: patientEncounterDetails.id,
                duplicateServiceOverrideInd:
                    showDuplicateServiceOverrideTextInput
                        ? patientEncounterDetails.duplicateServiceOverrideInd
                        : "",
            })
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update duplicate service override property, please refresh the page and try again and contact support if the issue persists.",
                    );
                else await reloadBillingDetails();
            });

        isUpdatingDuplicateServiceOverride = false;

        _closeChangeDuplicateServiceOverrideModal();
    };

    const handleDownloadPatientClaimStatement = () => {
        goToDownloadPage(16, billingDetails.patientEncounterId);
    };

    const initShowUnderAndOverPaymentText = (encounter) => {
        if (!encounter || isObjectEmpty(encounter)) return;

        const res = getUnderAndOverPaymentProperties(encounter);

        showUnderPaymentText = res.showUnderPayment;
        showOverPaymentText = res.showOverPayment;
        underPaymentTexts = res.underPaymentTexts;
        overPaymentTexts = res.overPaymentTexts;
    };

    const handleResetUnderOverPayment = async () => {
        isResettingUnderOverPayment = true;

        await apiMethods.putMethod
            .resetEncounterUnderOverPayment(
                underOverPaymentType,
                patientEncounterDetails.id,
                underOverPaymentFor,
                underOverPaymentAmount,
            )
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            `Failed to reset ${underOverPaymentType.toString().toLowerCase()}, please try again later or contact support...`,
                    );
                else reloadBillingDetails();
            });

        isResettingUnderOverPayment = false;

        _closeUnderOverPaymentModal();
    };

    // HELPER FUNCTIONS
    const _toggleShowCreatePartialPaymentBtn = (
        quoteData = {},
        invoiceData = {},
    ) => {
        if (!isObjectEmpty(invoiceData))
            return invoiceData.rebate > 0 && !invoiceData.patientPaysTotalFee;

        if (!isObjectEmpty(quoteData))
            return (
                quoteData.oopType == 0 &&
                quoteData.rebate > 0 &&
                !quoteData.patientPaysTotalFee
            );

        return false;
    };

    const _handleSendClaimStatusNotification = async (res) => {
        const result = JSON.stringify(res);

        if (result == "true") {
            _openNotificationSendSuccessModal();
            await reloadBillingDetails();
        } else
            showError(
                res.error ??
                    "Failed to send notification to patient, please refresh the page and try again and contact support if the issue persists.",
            );
    };

    const _openNotifyClaimStatusModal = () =>
        (isClaimStatusNotificationModalOpen = true);

    const _closeNotifyClaimStatusModal = () =>
        (isClaimStatusNotificationModalOpen = false);

    const _openNotificationSendSuccessModal = () =>
        (isSentNotificationSuccessModalOpen = true);

    const _closeNotificationSendSuccessModal = () =>
        (isSentNotificationSuccessModalOpen = false);

    const _openStopDailyRetrieveReportModal = () =>
        (isStopDailyRetrieveReportModalOpen = true);

    const _closeStopDailyRetrieveReportModal = () =>
        (isStopDailyRetrieveReportModalOpen = false);



    const _openChangeDuplicateServiceOverrideModal = () => {
        showDuplicateServiceOverrideTextInput = hasDuplicateServiceOverrideInd;

        showChangeDuplicateServiceOverrideModal = true;
    };

    const _closeChangeDuplicateServiceOverrideModal = () =>
        (showChangeDuplicateServiceOverrideModal = false);

    const getWorkCoverData = (workCoverAuthority) => {
        const workCoverData = $workersCompensation.find(
            (x) => x.id === workCoverAuthority,
        );
        return {
            email: workCoverData?.email ?? "",
            name: workCoverData?.name ?? "",
        };
    };

    const _showClaimStatusError = () => (showClaimStatusErrorMsg = true);

    const _determineVisibilityOfDuplicateServiceOverrideText = (
        billingData = {},
    ) => {
        if (!billingData || isObjectEmpty(billingData)) return false;

        const { insuranceType, patientPaysTotalFee } = billingData;

        const showText =
            insuranceType == DirectInsuranceType.DVA ||
            insuranceType == DirectInsuranceType.Private ||
            (insuranceType == DirectInsuranceType.SelfInsured &&
                !patientPaysTotalFee);

        return showText;
    };

    const _openResetUnderOverPaymentModal = (
        title,
        value,
        isUnderPayment = true,
    ) => {
        if (!isUserAdmin) return;

        const type = title.toString().replace(" Underpaid", "").toLowerCase();
        const amount = Number(
            value.toString().replace("$", "").replace(",", ""),
        ).toFixed(2);

        underOverPaymentType = isUnderPayment ? "Underpayment" : "Overpayment";
        underOverPaymentAmount = amount;
        underOverPaymentFor = type.includes("patient") ? Number(0) : Number(2); // 0 - patient, 2 - health fund
        showResetUnderOverPaymentModal = true;
    };

    const _closeUnderOverPaymentModal = () => {
        underOverPaymentType = "";
        underOverPaymentAmount = 0;
        underOverPaymentFor = -1;
        showResetUnderOverPaymentModal = false;
    };
   
</script>

<CardLayout title="Encounter Billing Status" {btnOptions}>
    <div
        class="flex flex-col flex-1 px-2 py-4 w-full overflow-x-auto lg:flex-row"
    >
        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div
                class="flex flex-row gap-x-2 {canUserEdit
                    ? 'cursor-pointer'
                    : ''}"
            >
                <p class="title">Billing Status:</p>

                {#if !isUpdatingBillingStatus}
                    <TableElementEditableStatus
                        type="patientEncounterBillingStatus"
                        row={{
                            id: billingDetails.patientEncounterId,
                            billingStatus: billingDetails
                                ? billingDetails.billingStatus
                                : 0,
                            scheduledDate:
                                patientEncounterDetails.scheduledDate,
                        }}
                        onFinish={() => reloadBillingDetails()}
                    />
                {/if}
            </div>

            {#if billingDetails.showHandOverDropdown}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Hand-Over:</p>
                    <TableElementEditableStatus
                        type="patientEncounterHandOverType"
                        row={{
                            id: billingDetails.patientEncounterId,
                            handOverType: billingDetails.handOver,
                            enableDoctorEdit: true,
                        }}
                        onFinish={() => reloadBillingDetails()}
                    />
                </div>
            {/if}

            <div class="flex flex-row gap-x-2">
                <p class="title">Skip Quote:</p>
                <TableElementEditableStatus
                    type="patientEncounterSkipQuote"
                    row={{
                        id: billingData.patientEncounterId,
                        skipQuote: patientEncounterDetails.skipQuote,
                    }}
                    onFinish={(e) => updateBillingSkipQuote(e)}
                />
            </div>

            {#if showNoGapProgram}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Is No-Gap Group:</p>
                    <p class="content">
                        {getBoolean(patientEncounterDetails.isNoGapProgram)}
                    </p>
                </div>
            {/if}
            {#if showDuplicateServiceOverrideText}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Duplicate Service Override:</p>
                    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <p
                        class="cursor-pointer underline"
                        on:click={_openChangeDuplicateServiceOverrideModal}
                    >
                        {hasDuplicateServiceOverrideInd ? "On" : "Off"}
                    </p>
                </div>
            {/if}

            {#if billingDetails.patientEncounterWorkCoverData}
                <div class="flex flex-row gap-x-2">
                    <p class="title">WorkCover Authority:</p>
                    {getWorkCoverData(
                        billingDetails.patientEncounterWorkCoverData
                            .workCoverAuthority,
                    ).name}
                </div>
                <div class="flex flex-row gap-x-2">
                    <p class="title">WorkCover Email:</p>
                    {getWorkCoverData(
                        billingDetails.patientEncounterWorkCoverData
                            .workCoverAuthority,
                    ).email}
                </div>
            {/if}

            {#if billingDetails.patientEncounterPIPData}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Billing Type:</p>
                    {getBillingType(
                        billingDetails.patientEncounterPIPData.billingType,
                    )}
                </div>
                <div class="flex flex-row gap-x-2">
                    <p class="title">Per Mbs Schedule Amount:</p>
                    {billingDetails.patientEncounterPIPData
                        .perMbsScheduleAmount}
                </div>
                <div class="flex flex-row gap-x-2">
                    <p class="title">Email:</p>
                    {billingDetails.patientEncounterPIPData.email}
                </div>
                <div class="flex flex-row gap-x-2">
                    <p class="title">Vendor Number:</p>
                    {billingDetails.patientEncounterPIPData.vendorNumbers}
                </div>
            {/if}

            {#if showUnderPaymentText}
                {#each underPaymentTexts as { title, value, isApprovedBy }}
                    <div class="flex flex-row gap-x-2">
                        <p class="title font-bold">
                            {isApprovedBy ? `Underpayment ${title}` : title}
                        </p>
                        <div class="flex gap-x-2">
                            <p>{value}</p>

                            <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                            {#if isUserAdmin && title && title
                                    .toString()
                                    .toLowerCase()
                                    .includes("underpaid")}
                                <!-- svelte-ignore a11y-click-events-have-key-events -->
                                <p
                                    class="underline cursor-pointer"
                                    on:click|preventDefault={() =>
                                        _openResetUnderOverPaymentModal(
                                            title,
                                            value,
                                        )}
                                >
                                    Reset
                                </p>
                            {/if}
                        </div>
                    </div>
                {/each}
            {/if}

            {#if showOverPaymentText}
                {#each overPaymentTexts as { title, value, isApprovedBy }}
                    <div class="flex flex-row gap-x-2">
                        <p class="title font-bold">
                            {isApprovedBy ? `Overpayment ${title}` : title}
                        </p>
                        <div class="flex gap-x-2">
                            <p>{value}</p>

                            <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                            {#if isUserAdmin && title && title
                                    .toString()
                                    .toLowerCase()
                                    .includes("overpaid")}
                                <!-- svelte-ignore a11y-click-events-have-key-events -->
                                <p
                                    class="underline cursor-pointer"
                                    on:click|preventDefault={() =>
                                        _openResetUnderOverPaymentModal(
                                            title,
                                            value,
                                            false,
                                        )}
                                >
                                    Reset
                                </p>
                            {/if}
                        </div>
                    </div>
                {/each}
            {/if}
        </div>

        {#if !isUserDoctor}
            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">Eligibility Status - Medicare:</p>
                    <p
                        class={`content ${
                            billingDetails.eligibilityStatusMedicare == 1
                                ? "text-greenDefault"
                                : billingDetails.eligibilityStatusMedicare == 2
                                  ? "text-red-400"
                                  : ""
                        }`}
                    >
                        {getEligibilityStatusMedicare(
                            billingDetails.eligibilityStatusMedicare,
                        )}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Eligibility Status - Health Fund:</p>
                    <p
                        class={`content ${
                            billingDetails.eligibilityStatusHealthFund == 1
                                ? "text-greenDefault"
                                : billingDetails.eligibilityStatusHealthFund ==
                                    2
                                  ? "text-red-400"
                                  : ""
                        }`}
                    >
                        {getEligibilityStatusHealthFund(
                            billingDetails.eligibilityStatusHealthFund,
                        )}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Eligibility Manually Approved:</p>
                    <p class="content">
                        {getBoolean(billingDetails.eligibilityManuallyApproved)}
                    </p>
                </div>

                <div class="flex flex-1 gap-x-12">
                    {#if showCheckEligibilityBtn}
                        <div class="flex flex-row gap-x-2">
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                            <p
                                class="cursor-pointer underline"
                                on:click|preventDefault={runEligibilityCheck}
                            >
                                Run Eligibility Check
                            </p>
                            {#if isCheckingEligibility}
                                <Loader />
                            {/if}
                        </div>
                    {/if}

                    <div class="flex flex-1 flex-col gap-y-2">
                        {#if showDownloadEligibilityDisclaimerBtn}
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                            <p
                                class={`underline ${
                                    isDownloadingEligibilityDoc
                                        ? "cursor-block"
                                        : "cursor-pointer"
                                }`}
                                on:click|preventDefault={downloadEligibilityDoc}
                            >
                                {isDownloadingEligibilityDoc
                                    ? "Downloading..."
                                    : "Download Eligibility Disclaimer Doc"}
                            </p>
                        {/if}

                        {#if showDownloadBulkBillAssignmentBtn}
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                            <p
                                class={`underline ${
                                    isDownloadingAssignmentDoc
                                        ? "cursor-block"
                                        : "cursor-pointer"
                                }`}
                                on:click|preventDefault={downloadAssignmentDoc}
                            >
                                {isDownloadingAssignmentDoc
                                    ? "Downloading..."
                                    : "Download Bulk Bill Assignment Doc"}
                            </p>
                        {/if}
                    </div>
                </div>
            </div>
        {/if}

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Claim Status:</p>

                <div class="content flex gap-x-1 items-center">
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                    <p
                        class={`${
                            billingDetails.claimStatus == 1
                                ? "text-red-400"
                                : billingDetails.claimStatus == 5
                                  ? "text-red-400 underline cursor-pointer"
                                  : billingDetails.claimStatus == 2
                                    ? "text-blue-400"
                                    : billingDetails.claimStatus == 3
                                      ? "text-greenDefault"
                                      : ""
                        }`}
                        on:click={() => {
                            if (billingDetails.claimStatus == 5)
                                _showClaimStatusError();
                        }}
                    >
                        {getClaimStatus(billingDetails.claimStatus)}

                        {#if showEncounterPendingClaimTooltip(patientEncounterDetails)}
                            <CustomTooltip
                                contentClass="bg-[#fff] border-none"
                                contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
                            >
                                <img
                                    src="/icons/alertIcon_gray.svg"
                                    alt="Info Icon"
                                    class="w-4 h-4"
                                    slot="tooltipTrigger"
                                />

                                <div class="flex flex-col gap-y-1">
                                    <p>
                                        {getEncounterClaimStatusTooltip(
                                            patientEncounterDetails,
                                        )}
                                    </p>
                                </div>
                            </CustomTooltip>
                        {/if}
                    </p>

                    {#if billingDetails && (billingDetails.claimStatus == 1 || billingDetails.claimStatus == 5)}
                        <CustomTooltip contentClass="border-red-400">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                class="h-6 w-6 pb-1 shrink-0 stroke-current text-red-400"
                                slot="tooltipTrigger"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                ></path>
                            </svg>

                            <p>
                                {billingDetails.claimLastError
                                    ? billingDetails.claimLastError
                                    : "No valid claim error details"}
                            </p>
                        </CustomTooltip>
                    {/if}
                </div>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Claim Last Try:</p>
                <p class="content">
                    {billingDetails.claimLastTry
                        ? getFormatDateAndTime(billingDetails.claimLastTry)
                        : ""}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Claim Attempts:</p>
                <p class="content">
                    {getText(billingDetails.claimTries, "-")}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">
                    {#if billingDetails.claimERAStatus == 10}
                        <InfoIcon position="top" tooltipClass="w-64" info={`This claim was stopped because the encounter was deleted, or the patient claim (PCI) completed successfully.`}></InfoIcon>
                    {/if}
                    Claim Payment Status:
                </p>
                <div
                    class="flex flex-1 gap-x-2 flex-wrap items-center justify-between"
                >
                    <div class="content flex gap-x-1 items-center">
                        <p
                            class={`${
                                billingDetails.claimERAStatus == 2
                                    ? "text-red-400"
                                    : billingDetails.claimERAStatus == 1
                                      ? "text-greenDefault"
                                      : billingDetails.claimERAStatus == 3
                                        ? "text-blue-400"
                                        : ""
                            }`}
                        >
                            {getClaimERAStatus(billingDetails.claimERAStatus)}
                        </p>

                        {#if billingDetails && billingDetails.claimERAStatus == 2}
                            <CustomTooltip contentClass="border-red-400">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    class="h-6 w-6 pb-1 shrink-0 stroke-current text-red-400"
                                    slot="tooltipTrigger"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    ></path>
                                </svg>

                                <p>
                                    {billingDetails.claimLastError
                                        ? billingDetails.claimLastError
                                        : "No valid claim error details"}
                                </p>
                            </CustomTooltip>
                        {/if}
                    </div>

                    {#if isStopClaimERAStatusBtnVisible}
                        <button
                            class="btn btn-xs btn-error"
                            on:click|preventDefault={_openStopDailyRetrieveReportModal}
                        >
                            Stop
                        </button>
                    {/if}
                </div>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Claim Transaction ID:</p>
                <p class="content">
                    {getText(billingDetails.claimTransactionId)}
                </p>
            </div>

            {#if billingDetails.patientClaim && billingData.insuranceType == 11 && [2, 3].includes(billingDetails.claimStatus)}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                <p
                    class="cursor-pointer underline hover:font-bold"
                    on:click={handleDownloadPatientClaimStatement}
                >
                    Download Statement
                </p>
            {/if}
           
        </div>
    </div>
</CardLayout>

<NotificationModal
    isModalOpen={isClaimStatusNotificationModalOpen}
    title="Apply insurance declined for this encounter?"
    cancelText="Cancel"
    submitText="Confirm"
    isConfirming={isSendingClaimStatusNotification}
    closeModal={_closeNotifyClaimStatusModal}
    confirmAction={sendClaimStatusNotification}
    closeModalFunction={_closeNotifyClaimStatusModal}
>
    The system will CANCEL ALL <i>pending quotes, invoices and payments</i>,
    change the encounter to total fee <i>post pay</i> and will send a new invoice
    for the full amount to the patient.
</NotificationModal>

<NotificationModal
    isModalOpen={isSentNotificationSuccessModalOpen}
    closeModal={_closeNotificationSendSuccessModal}
    showConfirmBtn={false}
    closeModalFunction={_closeNotificationSendSuccessModal}
>
    Successfully sent notification to patient.
</NotificationModal>

<NotificationModal
    isModalOpen={isStopDailyRetrieveReportModalOpen}
    cancelText="Cancel"
    submitText="Confirm"
    isConfirming={isUpdatingStopDailyRetrieveReport}
    closeModal={_closeStopDailyRetrieveReportModal}
    confirmAction={stopDailyRetrieveReport}
    closeModalFunction={_closeStopDailyRetrieveReportModal}
>
    Do you want to stop the daily retrieve reports for the encounter?
</NotificationModal>

<NotificationModal
    isModalOpen={isPartialPaymentCreated}
    closeModal={() => (isPartialPaymentCreated = false)}
    showConfirmBtn={false}
    closeModalFunction={() => (isPartialPaymentCreated = false)}
>
    TopUp Invoice has been created and sent to the patient.
</NotificationModal>



<NotificationModal
    bind:isModalOpen={showClaimStatusErrorMsg}
    title="Claim Status Error"
    showCancelBtn={false}
    confirmAction={() => (showClaimStatusErrorMsg = false)}
    submitText="Close"
    closeModalFunction={() => (showClaimStatusErrorMsg = false)}
>
    {billingDetails.claimLastError
        ? billingDetails.claimLastError
        : "No Error Message"}
</NotificationModal>

<NotificationModal
    isModalOpen={showChangeDuplicateServiceOverrideModal}
    cancelText="Cancel"
    submitText={isUpdatingDuplicateServiceOverride ? "Updating" : "Update"}
    closeModal={_closeChangeDuplicateServiceOverrideModal}
    confirmAction={updateDuplicateServiceOverride}
    isConfirming={isUpdatingDuplicateServiceOverride}
    title="Duplicate Service Override"
>
    <div class="flex flex-col gap-y-2">
        <div class="flex w-full gap-x-2">
            <p>Claim with Duplicate Service Override:</p>
            <input
                type="checkbox"
                class="toggle toggle-secondary"
                bind:checked={showDuplicateServiceOverrideTextInput}
            />
        </div>

        {#if showDuplicateServiceOverrideTextInput}
            <p>Service Description:</p>
            <input
                type="text"
                class="input input-sm input-bordered w-full"
                bind:value={patientEncounterDetails.duplicateServiceOverrideInd}
            />
        {/if}
    </div>
</NotificationModal>

{#if isUpdatingSkipQuote}
    <LoadingModal titleText="Updating Skip Quote value..." />
{/if}

<NotificationModal
    isModalOpen={showResetUnderOverPaymentModal}
    title={`Reset ${underOverPaymentType}`}
    showCancelBtn={false}
    showConfirmBtn={false}
    closeModalFunction={_closeUnderOverPaymentModal}
    isConfirming={isResettingUnderOverPayment}
    btnOptions={[
        {
            text: "Cancel",
            class: "btn-accent",
            loading: isResettingUnderOverPayment,
            function: () => _closeUnderOverPaymentModal(),
        },
        {
            text: isResettingUnderOverPayment ? "Resetting" : "Reset",
            class: "btn-error",
            loading: isResettingUnderOverPayment,
            function: () => handleResetUnderOverPayment(),
        },
    ]}
>
    <div class="flex flex-1 p-4 gap-x-2 items-center">
        <p>Set the {underOverPaymentType.toString().toLowerCase()} flag to</p>

        <DecimalInput bind:value={underOverPaymentAmount} />
    </div>
</NotificationModal>

<style>
    .title {
        width: 240px;
    }

    .content {
        width: calc(100% - 240px);
    }
</style>
